import { formatEventDateKey, parseDepositSchedule } from './payment-schedule.js';
/** Calculate using integer satang so repeated partial payments do not drift. */
export function money(value) {
    const n = Number(String(value ?? '').replace(/,/g, '').trim());
    return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
}
export function parsePaymentLedger(raw) {
    if (!raw)
        return null;
    try {
        const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (data?.version !== 1 || !Array.isArray(data.entries))
            return null;
        const ids = new Set();
        const entries = [];
        for (const row of data.entries) {
            if (!row?.id || ids.has(String(row.id)) || money(row.amount) <= 0)
                return null;
            ids.add(String(row.id));
            entries.push({
                id: String(row.id), installmentId: String(row.installmentId || ''),
                amount: money(row.amount), paidDate: formatEventDateKey(row.paidDate),
                method: String(row.method || ''), reference: String(row.reference || ''),
                note: String(row.note || ''), slipUrl: String(row.slipUrl || ''),
                createdAt: String(row.createdAt || ''),
                ...(row.voidedAt ? { voidedAt: String(row.voidedAt), voidReason: String(row.voidReason || '') } : {}),
            });
        }
        if (money(data.openingReceived) < 0)
            return null;
        return { version: 1, openingReceived: money(data.openingReceived), entries };
    }
    catch {
        return null;
    }
}
export function ledgerPaidTotal(ledger) {
    return money(ledger.openingReceived + ledger.entries.reduce((sum, row) => sum + (row.voidedAt ? 0 : row.amount), 0));
}
export function getJobPaidTotal(job) {
    const ledger = parsePaymentLedger(job['Payment Ledger']);
    return ledger ? ledgerPaidTotal(ledger) : Math.max(0, money(job['Actual Received']));
}
export function summarizePayments(totalRaw, installments, ledger, todayKey, withholdingPercent = 0) {
    const total = Math.max(0, money(totalRaw));
    const whtPct = Math.min(100, Math.max(0, money(withholdingPercent)));
    const whtMultiplier = 1 - whtPct / 100;
    // A deposit plan uses one job-level withholding gross-up, not a separate
    // checkbox/fee on every installment. If the plan has no explicit final row,
    // keep the gross-up in the automatically calculated remaining balance.
    const extraPlanned = installments.length > 0 && whtPct > 0 && whtMultiplier > 0
        ? money(total / whtMultiplier - total)
        : 0;
    const totalDue = money(total + extraPlanned);
    const paid = ledgerPaidTotal(ledger);
    let finalInstallmentIndex = -1;
    installments.forEach((row, index) => { if (row.kind === 'final')
        finalInstallmentIndex = index; });
    let scheduledSoFar = 0;
    const rounds = installments.map((row, index) => {
        const amount = money(row.amount);
        const extraAmount = index === finalInstallmentIndex ? extraPlanned : 0;
        const grossAmount = money(amount + extraAmount);
        scheduledSoFar = money(scheduledSoFar + grossAmount);
        const id = row.id || `legacy-round-${index + 1}`;
        const received = money(ledger.entries.reduce((sum, entry) => sum + (!entry.voidedAt && entry.installmentId === id ? entry.amount : 0), 0));
        const outstanding = Math.max(0, money(grossAmount - received));
        const overdue = outstanding > 0 && !!row.dueDate && row.dueDate < todayKey;
        return { ...row, id, extraAmount, grossAmount, received, outstanding, remainingAfterRound: Math.max(0, money(totalDue - scheduledSoFar)), status: outstanding === 0 && grossAmount > 0 ? 'paid' : overdue ? 'overdue' : received > 0 ? 'partial' : 'unpaid' };
    });
    const next = rounds.filter((r) => r.outstanding > 0 && r.dueDate).sort((a, b) => a.dueDate.localeCompare(b.dueDate))[0];
    return {
        total, totalDue, extraPlanned, paid, remaining: Math.max(0, money(totalDue - paid)), overpaid: Math.max(0, money(paid - totalDue)),
        planned: money(installments.reduce((sum, row) => sum + money(row.amount), 0)),
        rounds, nextDueDate: next?.dueDate || '',
        unassignedPaid: money(ledger.openingReceived + ledger.entries.reduce((sum, r) => sum + (!r.voidedAt && !r.installmentId ? r.amount : 0), 0)),
    };
}
/** Existing transactions cannot silently disappear or be rewritten by a stale form. */
export function mergePaymentLedgers(previous, incoming) {
    if (!previous)
        return incoming;
    if (money(previous.openingReceived) !== money(incoming.openingReceived)) {
        throw new Error('ยอดรับเงินเดิมเปลี่ยนไป กรุณาเปิดงานใหม่และตรวจสอบยอด');
    }
    const merged = new Map(previous.entries.map((row) => [row.id, row]));
    for (const row of incoming.entries) {
        const old = merged.get(row.id);
        if (old) {
            const { voidedAt: _oldVoid, voidReason: _oldReason, ...oldBase } = old;
            const { voidedAt: _newVoid, voidReason: _newReason, ...newBase } = row;
            if (JSON.stringify(oldBase) !== JSON.stringify(newBase))
                throw new Error('รายการรับเงินเดิมถูกแก้ไข กรุณายกเลิกรายการและเพิ่มรายการที่ถูกต้อง');
            if (old.voidedAt)
                continue;
        }
        merged.set(row.id, row);
    }
    return { version: 1, openingReceived: previous.openingReceived, entries: [...merged.values()] };
}
export function validatePaymentData(job, todayKey, withholdingPercent = 0) {
    const ledger = parsePaymentLedger(job['Payment Ledger']);
    if (!ledger)
        return job['Payment Ledger'] ? 'ข้อมูลการรับเงินไม่ถูกต้อง' : '';
    const rounds = parseDepositSchedule(job['Deposit Schedule']);
    const ids = new Set(rounds.map((r, i) => r.id || `legacy-round-${i + 1}`));
    if (ids.size !== rounds.length)
        return 'รหัสงวดชำระซ้ำกัน กรุณาตรวจสอบ';
    if (rounds.some((r) => !r.dueDate))
        return 'กรุณากำหนดวันครบกำหนดให้ทุกงวด';
    const summary = summarizePayments(job['Price per Unit'], rounds, ledger, todayKey, withholdingPercent);
    if (summary.planned > summary.total)
        return 'ยอดรวมงวดชำระเกินยอดเต็มทั้งงาน';
    if (summary.rounds.some((row) => row.received > row.grossAmount))
        return 'ยอดรับเงินจริงเกินยอดที่กำหนดในงวดรวมส่วนเพิ่ม';
    if (summary.overpaid > 0)
        return 'ยอดรับเงินจริงเกินยอดเต็มรวมส่วนเพิ่ม';
    for (const row of ledger.entries) {
        if (!row.paidDate || row.paidDate > todayKey)
            return 'วันที่รับเงินจริงต้องไม่เป็นวันในอนาคต';
        if (row.installmentId && !ids.has(row.installmentId))
            return 'งวดที่อ้างอิงในรายการรับเงินหายไป';
        if (row.voidedAt && !row.voidReason?.trim())
            return 'กรุณาระบุเหตุผลในการยกเลิกรายการรับเงิน';
        if (row.slipUrl && !/^https:\/\//i.test(row.slipUrl))
            return 'ลิงก์หลักฐานต้องเริ่มด้วย https://';
    }
    return '';
}
//# sourceMappingURL=payment-ledger.js.map